// is-select.component.ts
import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Inject,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    Renderer2,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { BehaviorSubject, Subscription, debounceTime, distinctUntilChanged, finalize, shareReplay, take } from 'rxjs';
import { ISAPI, ISFilterAPI, ISPagination, MVFilter, MVListingConfigs, MVOrdering, MVPagination } from '../models/api';
import { HttpMethod, ISSelectDatasource } from './is-select';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FilterOptions, ISColumn } from '../is-table/is-table';
import { MatMenuTrigger } from '@angular/material/menu';
import { RequestReplayService } from '../services/requestReplay.service';
import { Router } from '@angular/router';
import { SpinnerService } from '../services/spinner.service';
import { SortDirection } from '../models/generic';

@Component({
    selector: 'is-select',
    templateUrl: './is-select.component.html',
    styleUrls: ['./is-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ISSelectComponent),
            multi: true,
        },
    ],
})
export class ISSelectComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor, OnChanges {
    // ---------------------------
    // ✅ Infinite scroll handler (attached to the real scroll container in HTML)
    // ---------------------------
    scrollHandler(event: Event) {
        const el = event.target as HTMLElement;

        if (!this.DataSource$.value.apiUrl) return;
        if (this.httpMethod !== 'POST') return;
        if (this.lastPage || this.showLoader || this.showPartialLoader) return;

        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining <= 40) this.getMoreData();
    }

    @Input() DataSource$: BehaviorSubject<ISSelectDatasource> = new BehaviorSubject<ISSelectDatasource>(new ISSelectDatasource([]));

    @Input() Options: any[] | undefined; // In case you don't want to configure the options in the datasource
    @Input() placeholder: string = ''; // Used to set the placeholder of the input
    @Input() httpMethod: HttpMethod = 'POST'; // Used to set httpMethod as get or post
    @Input() name: string = ''; // Used to identity each instance of the component, useful when theres multiple ones
    @Input() smallInput: boolean = true;
    @Input() resetValues: boolean = true;
    @Input() requestOnFocus: boolean = false; // Used to determine if IS Select makes the request upon the select being focused instead of when the component is loaded
    @Input() useRequestReplay: boolean = false; // Determines if the requests made to the API are saved and their values replayed next time the select is loaded
    @Input() disabled: boolean = false; // Used to set the disabled state of the input
    @Input() openValues: boolean = false; // Used to determine if IS Select admits more values than the ones already in body
    @Input() translateData: boolean = true; // Used to determine if the select data is to be translated by the pipe
    @Input() solid: boolean = true;
    @Input() valid: boolean = false;
    @Input() invalid: boolean = false;
    @Input() menuFitInput: boolean = false;
    @Input() keyField?: string;
    @Input() noSearchInput: boolean = false; 

    @Input() queryable: boolean = false;

    @Input() menuTemplate?: TemplateRef<any>;

    @Input() componentId?: string = undefined;
    @Output() componentIdChange = new EventEmitter<string>();

    @Output() onSearchFilterInput: EventEmitter<any> = new EventEmitter<any>(); // Used to emit search filter string after debounce time
    @Output() onSelectedOption: EventEmitter<any> = new EventEmitter<any>(); // Used to emit the action button click event when an option is selected
    @Output() onReceiveDataFromAPI: EventEmitter<any> = new EventEmitter<any>(); // Used to emit when the datasource receives the data from the api

    /**
     * ✅ Eye button (view selected record)
     * - If true, shows an eye button when there is a selected record.
     * - Clicking emits the selected item so the parent can open a dialog with the record.
     */
    @Input() showViewButton: boolean = false;
    @Output() onViewSelectedOption: EventEmitter<any> = new EventEmitter<any>();

    // ✅ Avatar support (base64 png)
    @Input() showAvatar: boolean = false;
    @Input() avatarField?: string; // nome do campo que contém base64 (png)

    @ViewChild('search') searchElement!: ElementRef; // Used to identify the search input element
    @ViewChild(MatMenuTrigger, { static: false }) matMenuTrigger!: MatMenuTrigger;

    @Input() minChars: number = 0; // Used to show list only when certain amount of characters are written
    minCharsWarning = false;

    // Allows the consuming application to show the loader when the data is being requested outside of this component
    @Input() set externalLoading(value: boolean) {
        if (this._externalLoading != value) {
            this._externalLoading = value;
            this.toggleExternalSpinner();
        }
    }
    get externalLoading(): boolean {
        return this._externalLoading;
    }
    private _externalLoading: boolean = false;

    auxList: any[] = []; // guarda resultados do BE (para minChars / reset)
    searchFilter = new FormControl();

    value: string | undefined; // selected key value

    inputFilter = new BehaviorSubject<string>(''); // current filter value

    onChange = (value: string) => { };
    onTouched: any = () => { };

    defaultValues: any[] = this.DataSource$.value.body ?? [];

    showLoader: boolean = false;
    showPartialLoader: boolean = false;

    lastPage: boolean = false;
    selectKey?: string;

    selectionModel = new SelectionModel(false, this.defaultValues);

    private _unsubscribe: Subscription[] = [];
    defaultPagination = new ISPagination(50, 1);
    requestRequired = false;

    private resolvedKeyIdentifier?: string;

    private searchSub1?: Subscription;
    private searchSub2?: Subscription;

    constructor(
        private _spinnerService: SpinnerService,
        private _httpClient: HttpClient,
        private _dialog: MatDialog,
        private _cd: ChangeDetectorRef,
        private _renderer: Renderer2,
        private _injector: Injector,
        private _requestReplayService: RequestReplayService,
        private _router: Router
    ) { }

    /**
     * Indicates if the main loader is shown, either from an internal request or from the externalLoading input
     */
    get isLoading(): boolean {
        return this.showLoader || this._externalLoading;
    }

    /**
     * Shows or hides the spinner according to the externalLoading input
     */
    private toggleExternalSpinner() {
        if (!this.selectKey) {
            return;
        }

        if (this._externalLoading) {
            this._spinnerService.showInComponentSpinner(this.selectKey);
        } else {
            this._spinnerService.hideInComponentSpinner(this.selectKey);
        }
    }

    ngOnInit(): void {
        this.setLanguage();

        if (this.Options) {
            // create columns by option keys
            const columns: ISColumn[] = [];
            columns.push(new ISColumn('value', '', new FilterOptions('')));
            columns.push(new ISColumn('label', '', new FilterOptions('')));

            if (Object.hasOwn(this.Options[0], 'style')) {
                columns.push(new ISColumn('style', '', new FilterOptions('')));
            }
            if (Object.hasOwn(this.Options[0], 'icon')) {
                columns.push(new ISColumn('icon', '', new FilterOptions('')));
            }

            this.DataSource$ = new BehaviorSubject<ISSelectDatasource>(new ISSelectDatasource(columns, this.Options));
        }

        this.selectionModel.clear();

        if (!this.DataSource$.value.apiUrl) {
            this.defaultValues = this.DataSource$.value.body ?? [];
        }

        if (!this.DataSource$.value.pagination) {
            this.DataSource$.value.pagination = this.defaultPagination;
        } else {
            this.DataSource$.value.pagination.page ??= 1;
            this.DataSource$.value.pagination.pageSize ??= 50;
        }

        this.searchFilterSubscription();
    }

    ngAfterViewInit() {
        this.getKeyIdentifier();

        const ngControl: NgControl | null = this._injector.get(NgControl, null);
        if (ngControl) {
            if (this.name == null || this.name === '') {
                this.name = ngControl.name?.toString() ?? '';
            }
        }

        this.selectKey = `select-${!String.isNullOrUndefinedOrWhitespace(this.name) ? '' + this.name : ''}${this._router.url.replace('/', '-')}`;

        // The external loading may have been set before the spinner key existed
        if (this._externalLoading) {
            this.toggleExternalSpinner();
        }

        this.componentIdChange.emit(this.componentId);

        if (this.requestOnFocus) {
            if (this.value != null) {
                this.filter();
            } else {
                this.requestRequired = true;
            }
        } else {
            this.filter();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['disabled']?.currentValue != changes['disabled']?.previousValue) {
            changes['disabled'].currentValue === true ? this.searchFilter.disable() : this.searchFilter.enable();
        }

        if (changes['Options']) {
            this.DataSource$.value.body = changes['Options'].currentValue;

            this.resolvedKeyIdentifier = undefined;
            const key = this.getKeyIdentifier();

            if (key && this.DataSource$.value.body && this.selectionModel.selected[0]) {
                const selectedKeyVal = String(this.selectionModel.selected[0]?.[key]);
                const match = this.DataSource$.value.body.find((el) => String(el?.[key]) === selectedKeyVal);
                if (match) this.onSelect(match);
            }
        }

        this._cd.detectChanges();
    }

    setLanguage() {
        if (!localStorage.getItem('lang')) {
            localStorage.setItem('lang', 'pt');
        }
    }

    // ---------------------------
    // ✅ NEW: emit view event (eye button)
    // ---------------------------
    emitViewSelected(clickEvent?: MouseEvent) {
        clickEvent?.stopPropagation();
        clickEvent?.preventDefault();

        const selected = this.selectionModel.selected?.[0];
        if (!selected) return;

        this.onViewSelectedOption.emit(selected);
    }

    // ---------------------------
    // ✅ Normalized filtering (case-insensitive + sem acentos)
    // ---------------------------
    private normalizeText(val: any): string {
        return (val ?? '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    private includesNormalized(text: any, query: string): boolean {
        if (String.isNullOrUndefinedOrWhitespace(query)) return true;
        return this.normalizeText(text).includes(this.normalizeText(query));
    }

    private filterLocal(data: any[], query: string, field: string): any[] {
        if (!data) return [];
        if (String.isNullOrUndefinedOrWhitespace(query)) return data;
        return data.filter((x) => this.includesNormalized(x?.[field], query));
    }

    // ---------------------------
    // ✅ Key resolution (Id/value/email/etc.)
    // ---------------------------
    private resolveKeyIdentifier(body?: any[]): string | undefined {
        if (this.keyField && body?.some((x) => x && Object.prototype.hasOwnProperty.call(x, this.keyField ?? ''))) {
            return this.keyField;
        }

        const ds = this.DataSource$.value;
        const columns = ds?.columns ?? [];

        const c0 = columns[0]?.identifier;
        if (c0 && body?.some((x) => x && Object.prototype.hasOwnProperty.call(x, c0))) {
            return c0;
        }

        const firstValidColumn = columns.find((c) => c?.identifier && body?.some((x) => x && Object.prototype.hasOwnProperty.call(x, c.identifier)));
        if (firstValidColumn?.identifier) return firstValidColumn.identifier;

        const firstItem = body?.find((x) => x && typeof x === 'object');
        if (firstItem) return Object.keys(firstItem)[0];

        return undefined;
    }

    private getKeyIdentifier(): string | undefined {
        const body = this.DataSource$.value.body ?? [];
        if (!this.resolvedKeyIdentifier) {
            this.resolvedKeyIdentifier = this.resolveKeyIdentifier(body);
        }
        return this.resolvedKeyIdentifier;
    }

    private getItemKeyValue(item: any): string {
        const key = this.getKeyIdentifier();
        if (!key) return '';
        const v = item?.[key];
        return v == null ? '' : String(v);
    }

    // ---------------------------
    // ✅ Search subscriptions (ONCE)
    // ---------------------------
    private searchFilterSubscription() {
        this.searchSub1?.unsubscribe();
        this.searchSub2?.unsubscribe();

        // while typing
        this.searchSub1 = this.searchFilter.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
            this.showLoader = true;
            this.minCharsWarning = false;
        });

        // after debounce
        this.searchSub2 = this.searchFilter.valueChanges.pipe(debounceTime(600)).subscribe((searchValue) => {
            this.onSearchFilterInput.emit(searchValue);

            const v = (searchValue ?? '').toString();

            if (v.length > 0) {
                this.inputFilter.next(v);

                if (this.minChars === 0) {
                    this.filterSearch(v);
                } else {
                    if (v.length >= this.minChars) this.filterSearch(v);
                    else {
                        this.showLoader = false;
                        this.minCharsWarning = true;
                    }
                }
            } else {
                if (this.minChars > 0) {
                    this.showLoader = false;
                    this.DataSource$.value.body = [];
                } else {
                    this.filterSearch('');
                }

                this.minCharsWarning = this.minChars > 0;
            }
        });
    }

    // ---------------------------
    // ✅ Filter entrypoint (kept to preserve behavior)
    // ---------------------------
    filter(event?: any) {
        this.inputFilter.next(event?.target?.value);

        if (this.minChars === 0) {
            if (!this.DataSource$.value.apiUrl) {
                if (this.defaultValues.length === 0) {
                    this.defaultValues = this.DataSource$.value.body!;
                }
                if (this.resetValues) {
                    this.DataSource$.value.body = this.defaultValues;
                }
                this.DataSource$.value.pagination.page = 1;

                if (this.DataSource$.value.columns[1]) {
                    this.DataSource$.value.columns[1].filterOptions.value = event?.target?.value ?? '';
                }

                const descField = this.DataSource$.value.columns[1].identifier;
                const base = this.resetValues && this.auxList.length > 0 ? this.auxList : this.DataSource$.value.body ?? [];
                this.DataSource$.value.body = this.filterLocal(base, event?.target?.value ?? '', descField);
                this.showLoader = false;
            } else {
                if (this.DataSource$.value.columns[1]) {
                    this.DataSource$.value.columns[1].filterOptions.value = event?.target?.value ?? '';
                }
                this.DataSource$.value.pagination.page = 1;
                this.getDataFromTheAPI(this.DataSource$.value.apiUrl, true, false, true);

                // GET brings all data; FE filters it
                if (this.httpMethod === 'GET') {
                    this.DataSource$.value.apiUrl = undefined;
                }
            }
        } else if (this.minChars > 0) {
            this.minCharsWarning = true;

            if (this.DataSource$.value.apiUrl && this.httpMethod === 'GET') {
                if (this.DataSource$.value.columns[1]) {
                    this.DataSource$.value.columns[1].filterOptions.value = event?.target?.value ?? '';
                }
                this.DataSource$.value.pagination.page = 1;
                this.getDataFromTheAPI(this.DataSource$.value.apiUrl, true, false, true);
                this.DataSource$.value.apiUrl = undefined;
            }
        }
    }

    // ---------------------------
    // ✅ filterSearch (local filtering normalized)
    // ---------------------------
    private filterSearch(searchValue: string) {
        this.minCharsWarning = false;

        const descField = this.DataSource$.value.columns[1]?.identifier;

        if (this.minChars === 0) {
            if (!this.DataSource$.value.apiUrl) {
                if (this.defaultValues.length === 0) {
                    this.defaultValues = this.DataSource$.value.body!;
                }
                if (this.resetValues) {
                    this.DataSource$.value.body = this.defaultValues;
                }
                this.DataSource$.value.pagination.page = 1;

                if (this.DataSource$.value.columns[1]) {
                    this.DataSource$.value.columns[1].filterOptions.value = searchValue ?? '';
                }

                if (descField) {
                    const base = this.resetValues && this.auxList.length > 0 ? this.auxList : this.DataSource$.value.body ?? [];
                    this.DataSource$.value.body = this.filterLocal(base, searchValue ?? '', descField);
                }

                this.showLoader = false;
            } else {
                if (this.DataSource$.value.columns[1]) {
                    this.DataSource$.value.columns[1].filterOptions.value = searchValue ?? '';
                }
                this.DataSource$.value.pagination.page = 1;
                this.getDataFromTheAPI(this.DataSource$.value.apiUrl, true, false, true);

                if (this.httpMethod === 'GET') {
                    this.DataSource$.value.apiUrl = undefined;
                }
            }
        } else if (this.minChars > 0) {
            if (!this.DataSource$.value.apiUrl) {
                if (this.defaultValues.length === 0) {
                    this.defaultValues = this.DataSource$.value.body!;
                }
                if (this.resetValues) {
                    this.DataSource$.value.body = this.defaultValues;
                }

                this.DataSource$.value.pagination.page = 1;

                if (this.DataSource$.value.columns[1]) {
                    this.DataSource$.value.columns[1].filterOptions.value = searchValue ?? '';
                }

                if (descField) {
                    const base = this.resetValues && this.auxList.length > 0 ? this.auxList : this.DataSource$.value.body ?? [];
                    this.DataSource$.value.body = this.filterLocal(base, searchValue ?? '', descField);
                }

                this.showLoader = false;
            } else {
                if (this.DataSource$.value.columns[1]) {
                    this.DataSource$.value.columns[1].filterOptions.value = searchValue ?? '';
                }
                this.DataSource$.value.pagination.page = 1;
                this.getDataFromTheAPI(this.DataSource$.value.apiUrl, true, false, true);
            }
        }
    }

    // ---------------------------
    // ✅ Infinite scroll (POST)
    // ---------------------------
    getMoreData() {
        if (this.DataSource$.value.apiUrl) {
            this.DataSource$.value.pagination.page++;
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl, false, true);
        }
    }

    openDialog() {
        if (this.DataSource$.value.codeBtnDialog) {
            this._dialog.open(DialogEntityInfoDialog, {
                data: this.selectionModel.selected[0],
            });
        }
    }

    onSelect(item: any) {
        this.onSelectedOption.emit(item);

        const keyValue = this.getItemKeyValue(item);
        this.onChange(keyValue);

        this.selectionModel.clear();
        this.selectionModel.select(item);

        this.matMenuTrigger.closeMenu();
    }

    deselect() {
        this.value = '';
        this.onChange('');
        this.selectionModel.clear();

        if (this.searchFilter.value != '') {
            this.searchFilter.setValue('');
        }
    }

    // ---------------------------
    // ✅ NEW: total-based lastPage calculation (preferred), with fallback heuristic
    // ---------------------------
    private updateTotalAndLastPage(res: any, lastBatchCount: number) {
        const total = res?.totalListCount ?? res?.TotalListCount ?? 0;
        this.DataSource$.value.totalItems = total;

        const loaded = this.DataSource$.value.body?.length ?? 0;

        if (total && total > 0) {
            this.lastPage = loaded >= total;
            return;
        }

        // Fallback when API doesn't return total
        if (lastBatchCount < this.DataSource$.value.pagination.pageSize) {
            this.lastPage = true;
        }
    }

    // ---------------------------
    // ✅ API request (GET/POST)
    // ---------------------------
    getDataFromTheAPI(APIUrl: string, resetData: boolean = true, scrollEvent: boolean = false, hasFilters: boolean = false) {
        let requestObservable: any = null;
        let table: ISAPI | MVListingConfigs;
        const pagination = this.DataSource$.value.pagination;

        if (scrollEvent) this.showPartialLoader = true;
        else this.showLoader = true;

        this._spinnerService.showInComponentSpinner(this.selectKey!);

        if (this.httpMethod === 'GET') {
            if (this.useRequestReplay) {
                requestObservable = this._requestReplayService.getRequestReplay(this.selectKey!, this.componentId);

                if (requestObservable == undefined) {
                    requestObservable = this._httpClient.get(APIUrl).pipe(shareReplay());
                    this._requestReplayService.saveRequestReplay(this.selectKey!, requestObservable, this.componentId);
                }
            } else {
                requestObservable = this._httpClient.get(APIUrl);
            }

            requestObservable
                .pipe(take(1), finalize(() => null/*this._spinnerService.hideInComponentSpinner(this.selectKey!)*/))
                .subscribe({
                    next: (res: any) => {
                        this.resolvedKeyIdentifier = undefined;
                        this.getKeyIdentifier();

                        if (resetData) {
                            this.DataSource$.value.body = [];
                            this.auxList = [];
                            this.lastPage = false;
                            this.DataSource$.value.pagination.page = 1;
                        }

                        const arr = this.extractArray(res);

                        if (arr.length > 0) {
                            if (this.minChars === 0) {
                                this.DataSource$.value.body?.push(...arr);
                                this.auxList.push(...arr);
                            } else {
                                this.auxList.push(...arr);
                            }
                        } else {
                            this.lastPage = true;
                        }

                        // total-aware lastPage + fallback
                        this.updateTotalAndLastPage(res, arr.length);

                        // FE filter for GET (because no server filtering)
                        const query = this.DataSource$.value.columns?.[1]?.filterOptions?.value ?? '';
                        const descField = this.DataSource$.value.columns?.[1]?.identifier;
                        if (this.minChars === 0 && descField) {
                            const base = this.resetValues && this.auxList.length > 0 ? this.auxList : this.DataSource$.value.body ?? [];
                            this.DataSource$.value.body = this.filterLocal(base, query, descField);
                        }

                        this.defaultValues = this.DataSource$.value.body ?? [];

                        if (scrollEvent) this.showPartialLoader = false;
                        else this.showLoader = false;

                        this.selectionModel = new SelectionModel(false, this.DataSource$.value.body ?? []);
                        this.selectionModel.clear();
                        this.onReceiveDataFromAPI.emit();

                        if (this.value) this.writeValue(this.value);
                    },
                    error: (err: HttpErrorResponse) => {
                        this.onReceiveDataFromAPI.emit(err.error?.statusCode + ' - ' + err.error?.message);
                        this.showLoader = false;
                        this.showPartialLoader = false;
                    },
                });

            return;
        }

        // POST
        if (this.queryable) {
            const mvFilters: MVFilter[] = [];

            this.DataSource$.value.columns.forEach((column: ISColumn) => {
                const filterVal = column.filterOptions?.value;
                if (filterVal !== null && filterVal !== undefined && filterVal !== '') {
                    mvFilters.push(
                        new MVFilter(
                            column.filterOptions?.searchField ?? column.identifier,
                            filterVal,
                            column.filterOptions?.type || 'text',
                            column.filterOptions?.operator || 'contains',
                            column.filterOptions?.navigation
                        )
                    );
                }
            });

            this.DataSource$.value.filters?.forEach((column: ISFilterAPI) => {
                const filterVal = column?.value;
                if (filterVal !== null && filterVal !== undefined && filterVal !== '') {
                    mvFilters.push(new MVFilter(column.searchField ?? column.identifier, filterVal, column?.type || 'text', column?.operator || 'contains', column?.navigation));
                }
            });

            const orderingColumn = this.DataSource$.value.columns.find((c) => c.direction && (c.direction === SortDirection.asc || c.direction === SortDirection.desc));
            const ordering = orderingColumn
                ? new MVOrdering(orderingColumn?.filterOptions.searchField ?? orderingColumn.identifier, orderingColumn?.direction, orderingColumn?.filterOptions?.navigation)
                : null;

            table = new MVListingConfigs(mvFilters, ordering, new MVPagination(pagination.page, pagination.pageSize));
        } else {
            table = new ISAPI([], pagination);

            this.DataSource$.value.columns.forEach((column: ISColumn) => {
                (table as ISAPI).filters.push({
                    identifier: column.identifier,
                    value: column.filterOptions?.value,
                    type: column.filterOptions?.type,
                    operator: column.filterOptions?.operator,
                    orderType: column.direction,
                    searchField: column.filterOptions?.searchField,
                    navigation: column.filterOptions?.navigation,
                });
            });

            this.DataSource$.value.filters?.forEach((filter) => {
                (table as ISAPI).filters.push(filter);
            });
        }

        if (this.useRequestReplay && !hasFilters) {
            requestObservable = this._requestReplayService.getRequestReplay(this.selectKey!, this.componentId);

            if (requestObservable == undefined) {
                requestObservable = this._httpClient.post(APIUrl, table).pipe(shareReplay());
                this._requestReplayService.saveRequestReplay(this.selectKey!, requestObservable, this.componentId);
            }
        } else {
            requestObservable = this._httpClient.post(APIUrl, table);
        }

        requestObservable.pipe(finalize(() => null/*this._spinnerService.hideInComponentSpinner(this.selectKey!)*/ )).subscribe({
            next: (res: any) => {
                this.resolvedKeyIdentifier = undefined;
                this.getKeyIdentifier();

                if (resetData) {
                    this.DataSource$.value.body = [];
                    this.auxList = [];
                    this.lastPage = false;
                    this.DataSource$.value.pagination.page = 1;
                }

                const arr = this.extractArray(res);

                // append / set
                if (scrollEvent) {
                    if (arr.length > 0) this.DataSource$.value.body?.push(...arr);
                    this.showPartialLoader = false;
                } else {
                    if (arr.length > 0) this.DataSource$.value.body?.push(...arr);
                    this.showLoader = false;
                }

                // keep auxList updated (useful for FE filtering if needed)
                this.auxList.push(...arr);

                this.defaultValues = this.DataSource$.value.body ?? [];

                // total-aware lastPage + fallback
                this.updateTotalAndLastPage(res, arr.length);

                this.selectionModel.clear();
                this.onReceiveDataFromAPI.emit();

                if (this.value) this.writeValue(this.value);
            },
            error: (err: HttpErrorResponse) => {
                this.onReceiveDataFromAPI.emit(err.error?.statusCode + ' - ' + err.error?.message);
                this.showLoader = false;
                this.showPartialLoader = false;
            },
        });
    }

    protected inputFocus(clickEvent?: MouseEvent) {
        setTimeout(() => {
            this.searchElement?.nativeElement.focus();
        }, 0);

        if (this.requestOnFocus && this.requestRequired) {
            this.filter();
            this.requestRequired = false;
        }

        if (this.menuFitInput) {
            const inputElement: HTMLElement | null = document.getElementById('input_' + this.componentId);

            if (inputElement) {
                const menuElement: HTMLElement | null = document.getElementById('menu_' + this.componentId);

                if (menuElement) {
                    if (inputElement.offsetWidth > 250) {
                        menuElement.parentElement!.parentElement!.style.maxWidth = inputElement.offsetWidth + 'px';
                        menuElement.style.width = inputElement.offsetWidth + 'px';
                    } else {
                        menuElement.parentElement!.parentElement!.style.maxWidth = '250px';
                        menuElement.style.width = '250px';
                    }
                }
            }
        }
    }

    public get inputId() {
        return 'input_' + this.componentId;
    }

    setFilterValue(value?: string) {
        this.searchFilter.setValue(value);
    }

    // ---------------------------
    // ✅ Avatar / Tooltip helpers for template
    // ---------------------------
    public getAvatarSrc(item: any): string | null {
        if (!this.showAvatar || String.isNullOrUndefinedOrWhitespace(this.avatarField)) return null;

        const b64 = item?.[this.avatarField!];
        if (!b64) return null;

        const str = b64.toString().trim();
        if (!str) return null;

        if (str.startsWith('data:image/')) return str;

        return `data:image/png;base64,${str}`;
    }

    public initials(text: string): string {
        const t = (text ?? '').trim();
        if (!t) return '?';
        const parts = t.split(/\s+/).filter(Boolean);
        const a = parts[0]?.[0] ?? '';
        const b = parts.length > 1 ? parts[1]?.[0] ?? '' : parts[0]?.[1] ?? '';
        return (a + b).toUpperCase();
    }

    public isTruncated(el: HTMLElement | null): boolean {
        if (!el) return false;
        return el.scrollWidth > el.clientWidth;
    }

    private extractArray(res: any): any[] {
        if (res == null) return [];

        if (res.data != null) return Array.isArray(res.data) ? res.data : [res.data];
        if (res.items != null) return Array.isArray(res.items) ? res.items : [res.items];
        if (res.body != null) return Array.isArray(res.body) ? res.body : [res.body];
        if (res.results != null) return Array.isArray(res.results) ? res.results : [res.results];
        if (Array.isArray(res)) return res;
        if (typeof res === 'object') return [res];

        return [];
    }

    // ---------------------------
    // ControlValueAccessor
    // ---------------------------
    writeValue(value: string): void {
        if (String.isNullOrUndefinedOrWhitespace(value)) {
            this.selectionModel.clear();
            this.value = value;
            return;
        }

        this.value = value;

        const key = this.getKeyIdentifier();
        if (!key) {
            this.selectionModel.clear();
            return;
        }

        const record = this.DataSource$.value.body?.find((x) => String(x?.[key]) === String(value));
        this.selectionModel.clear();
        if (record) this.selectionModel.select(record);
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (isDisabled) {
            this.searchFilter.setValue(null, { emitEvent: false });
            this.searchFilter.disable({ emitEvent: false });
        } else {
            this.searchFilter.setValue(null, { emitEvent: false });
            this.searchFilter.enable({ emitEvent: false });
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    ngOnDestroy(): void {
        this._unsubscribe.forEach((sub) => sub.unsubscribe());
        this.searchSub1?.unsubscribe();
        this.searchSub2?.unsubscribe();
    }
}

@Component({
    selector: 'dialog-entity-info',
    template: `<div class="m-12"><h1>{{ data | json }}</h1></div>`,
})
export class DialogEntityInfoDialog {
    constructor(@Inject(MAT_DIALOG_DATA) public data: any) { }
}